import type { GoogleSearchConsoleSite, Project } from '@shared/api-client';

export const normalizeTerm = (term: string) =>
  term.trim().replace(/\s+/g, ' ').toLowerCase();
export const uniqueTerms = (terms: string[]) => {
  const unique = new Map<string, string>();
  for (const term of terms) {
    const key = normalizeTerm(term);
    if (key && !unique.has(key)) {
      unique.set(key, term.trim().replace(/\s+/g, ' '));
    }
  }
  return [...unique.values()];
};

export const mergeListeningTerms = (
  current: string[],
  selected: string[],
  automatic: string[],
) => {
  const excluded = new Set(automatic.map(normalizeTerm));
  const merged = uniqueTerms([
    ...current,
    ...selected.filter((term) => !excluded.has(normalizeTerm(term))),
  ]);
  if (merged.some((term) => term.length < 2 || term.length > 80)) {
    throw new Error('Use 2–80 characters for each keyword.');
  }
  if (merged.length > 20) {
    throw new Error(
      'You can listen for up to 20 additional keywords. Choose fewer to continue.',
    );
  }
  return merged;
};

/** Suggest only a verified property that covers this project's actual website. */
export const matchingSearchConsoleSite = (
  project: Project,
  sites: GoogleSearchConsoleSite[],
): string => {
  if (!project.profile?.websiteUrl && !project.primaryDomain) {
    return '';
  }
  let target: URL;
  try {
    target = new URL(
      project.profile?.websiteUrl || `https://${project.primaryDomain}`,
    );
  } catch {
    return '';
  }
  const domain = sites
    .filter((site) => {
      if (
        site.permissionLevel === 'siteUnverifiedUser' ||
        !site.siteUrl.startsWith('sc-domain:')
      ) {
        return false;
      }
      const host = site.siteUrl.slice(10).toLowerCase();
      return target.hostname === host || target.hostname.endsWith(`.${host}`);
    })
    .sort((a, b) => b.siteUrl.length - a.siteUrl.length)[0];
  if (domain) {
    return domain.siteUrl;
  }
  return (
    sites
      .filter((site) => {
        if (site.permissionLevel === 'siteUnverifiedUser') {
          return false;
        }
        try {
          const property = new URL(site.siteUrl);
          return (
            target.origin === property.origin &&
            (target.pathname === property.pathname ||
              target.pathname.startsWith(
                property.pathname.endsWith('/')
                  ? property.pathname
                  : `${property.pathname}/`,
              ))
          );
        } catch {
          return false;
        }
      })
      .sort((a, b) => b.siteUrl.length - a.siteUrl.length)[0]?.siteUrl ?? ''
  );
};
